/**
 * ledgerFilters.js — pure, stateless filter logic for the LedgerDashboardPage.
 *
 * applyLedgerFilters(portals, filters) -> portals
 *
 * Each portal object has the shape:
 *   { portal, total_nav, funds[] }
 *
 * Each fund has:
 *   { fund_name, currency, portal, kit:{class,...}, members[], total_commitment,
 *     total_distributed, total_nav }
 *
 * Each member has:
 *   { portal, fund_name, investor_name, capital_contributed_cumulative,
 *     distributions_cumulative, nav, tvpi, dpi, irr, irr_reliable,
 *     is_exited, source_doctypes:[], first_activity, last_activity, discarded, ... }
 *
 * filters shape:
 *   {
 *     portal:      string   — exact match on portal id (empty = all portals)
 *     fund:        string   — exact match on fund_name (empty = all)
 *     investor:    string   — punctuation-insensitive substring on investor_name (see foldForMatch/foldTight)
 *     currency:    string   — exact match on fund.currency
 *     assetClass:  string   — exact match on fund.kit.class
 *     docTypes:    string[] — member matches if source_doctypes ∩ docTypes ≠ ∅
 *     dateFrom:    string   — YYYY-MM-DD; member matches if last_activity >= dateFrom
 *     dateTo:      string   — YYYY-MM-DD; member matches if first_activity <= dateTo
 *     showExited:  boolean  — when false, hide exited members (default: true = show all)
 *     search:      string   — punctuation-insensitive substring on fund_name, fund_display_name,
 *                              OR investor_name (see foldForMatch/foldTight)
 *   }
 *
 * Empty / falsy values mean "no filter on this axis".
 * Returns a new portals array (deep structural copy of relevant parts, members filtered).
 * Empty funds (0 visible members) and empty portals (0 visible funds) are dropped.
 */

/**
 * Fold a string for punctuation-insensitive matching.
 *
 * The v3 ledger's identity strings (fund_name, investor_name) are NORMALIZED
 * SLUGS — lowercased with all punctuation folded to spaces (e.g. fund_name
 * "noked opportunity cayman l p"). Clients type what they SEE, which is the
 * punctuated display name ("Noked Opportunity (Cayman) L.P."). A raw
 * substring match against the slug therefore fails whenever the typed text
 * contains punctuation the slug lost (periods, commas, parens, hyphens,
 * Hebrew gershayim, etc.). Folding both sides the same way before comparing
 * makes punctuation irrelevant to matching, on any script (Unicode-aware).
 *
 * This alone still misses dotted acronyms: "I.B.I" space-folds to "i b i",
 * which never substring-matches the tight slug form "ibi" (or vice versa —
 * a tight typed query like "ibi" won't match a slug that kept the letters
 * spaced apart, "i b i clo strategies fund lp"). See foldTight below, which
 * textMatches also checks, so either spacing style matches either way.
 */
export const foldForMatch = (s) =>
  String(s || "").toLowerCase().replace(/[^\p{L}\p{N}]+/gu, " ").trim();

/**
 * Fold a string for punctuation- AND space-insensitive matching.
 *
 * Same idea as foldForMatch but collapses everything (punctuation AND
 * whitespace) instead of folding punctuation to a space. This lets a tight
 * acronym typed by the client ("IBI") cross-match a slug that kept the
 * letters space-separated ("i b i clo ..."), and a dotted/spaced query
 * ("I.B.I") cross-match a slug that kept them tight ("ibi clo ...").
 */
export const foldTight = (s) =>
  String(s || "").toLowerCase().replace(/[^\p{L}\p{N}]+/gu, "");

/**
 * Punctuation-insensitive "does haystack contain needle" check.
 * Callers must guard on a non-empty trimmed needle before calling this —
 * an all-punctuation needle folds to "", and "".includes("") is true, which
 * would otherwise match everything.
 *
 * Tries both fold styles (space-preserving and tight) and accepts either —
 * this is a strict superset of a foldForMatch-only match, so nothing that
 * matched before can stop matching.
 */
const textMatches = (haystack, needle) =>
  foldForMatch(haystack).includes(foldForMatch(needle)) ||
  foldTight(haystack).includes(foldTight(needle));

/**
 * Test whether a member's [first_activity, last_activity] date range overlaps
 * the [dateFrom, dateTo] filter window.
 *
 * A member with both activities null is treated as having an unknown range
 * and passes the date filter (we don't hide data we can't date).
 *
 * Overlap condition: member.first_activity <= dateTo AND member.last_activity >= dateFrom
 * When one side of either range is null, that bound is treated as unbounded.
 */
export function memberMatchesDateRange(member, dateFrom, dateTo) {
  if (!dateFrom && !dateTo) return true;

  const memberFirst = member.first_activity || null;
  const memberLast = member.last_activity || null;

  // If we have no activity data at all, pass through (don't silently hide)
  if (!memberFirst && !memberLast) return true;

  if (dateTo && memberFirst) {
    // member started after the window ends — no overlap
    if (memberFirst > dateTo) return false;
  }
  if (dateFrom && memberLast) {
    // member ended before the window starts — no overlap
    if (memberLast < dateFrom) return false;
  }

  return true;
}

/**
 * Test whether a member's source_doctypes array intersects the selected doc types.
 * If no docTypes selected, always passes.
 */
export function memberMatchesDocTypes(member, docTypes) {
  if (!docTypes || docTypes.length === 0) return true;
  const memberTypes = Array.isArray(member.source_doctypes) ? member.source_doctypes : [];
  return docTypes.some((dt) => memberTypes.includes(dt));
}

/**
 * Apply all filters to a single member row.
 */
function memberPasses(member, filters) {
  // Investor substring filter (punctuation-insensitive — see textMatches)
  if (filters.investor && filters.investor.trim() !== "") {
    if (!textMatches(member.investor_name, filters.investor)) return false;
  }

  // Doc type filter
  if (!memberMatchesDocTypes(member, filters.docTypes)) return false;

  // Date range overlap
  if (!memberMatchesDateRange(member, filters.dateFrom || "", filters.dateTo || "")) return false;

  // Exited toggle — when showExited is explicitly false, hide exited members
  if (filters.showExited === false && member.is_exited) return false;

  // Search (fund_name OR fund_display_name OR investor_name), punctuation-insensitive
  if (filters.search && filters.search.trim() !== "") {
    const inFund = textMatches(member.fund_name, filters.search);
    const inFundDisplay = textMatches(member.fund_display_name, filters.search);
    const inInvestor = textMatches(member.investor_name, filters.search);
    if (!inFund && !inFundDisplay && !inInvestor) return false;
  }

  // XIRR range filter — inputs are percent (e.g. -20 to 30); irr is decimal (e.g. 0.0878).
  // Requires irr_reliable===true and irr != null when either bound is set.
  const hasXirrMin = filters.xirrMin != null && filters.xirrMin !== "";
  const hasXirrMax = filters.xirrMax != null && filters.xirrMax !== "";
  if (hasXirrMin || hasXirrMax) {
    if (member.irr_reliable !== true || member.irr == null) return false;
    const irrDecimal = Number(member.irr);
    if (hasXirrMin && irrDecimal < Number(filters.xirrMin) / 100) return false;
    if (hasXirrMax && irrDecimal > Number(filters.xirrMax) / 100) return false;
  }

  return true;
}

/**
 * Apply all filters to a single fund.
 * Returns null if the fund should be hidden entirely (wrong currency, asset class,
 * fund name filter, or no members survive).
 * Returns a new fund object with filtered members list otherwise.
 */
function filterFund(fund, filters) {
  // Fund-level filters
  if (filters.fund && filters.fund !== "" && fund.fund_name !== filters.fund) return null;

  if (filters.currency && filters.currency !== "") {
    const fundCcy = String(fund.currency || "").trim().toUpperCase();
    if (fundCcy !== filters.currency.trim().toUpperCase()) return null;
  }

  if (filters.assetClass && filters.assetClass !== "") {
    const kitClass = (fund.kit && fund.kit.class) ? fund.kit.class : "";
    if (kitClass !== filters.assetClass) return null;
  }

  // Search at fund level too (fund_name OR fund_display_name), punctuation-insensitive
  if (filters.search && filters.search.trim() !== "") {
    const fundNameMatches =
      textMatches(fund.fund_name, filters.search) ||
      textMatches(fund.fund_display_name, filters.search);
    // If fund identity matches, all non-discarded members pass through (search matches
    // the fund) — member.fund_display_name is often absent, so we can't rely on the
    // generic member-level search check below to reproduce this fund-level match.
    if (fundNameMatches) {
      const visibleMembers = (fund.members || []).filter(
        (m) => !m.discarded && memberPasses(m, { ...filters, search: "" })
      );
      if (visibleMembers.length === 0) return null;
      return { ...fund, members: visibleMembers };
    }
    // Fund name doesn't match — filter members individually
    const visibleMembers = (fund.members || []).filter(
      (m) => !m.discarded && memberPasses(m, { ...filters, search: "" })
        && textMatches(m.investor_name, filters.search)
    );
    if (visibleMembers.length === 0) return null;
    return { ...fund, members: visibleMembers };
  }

  // Filter members
  const visibleMembers = (fund.members || []).filter(
    (m) => !m.discarded && memberPasses(m, filters)
  );

  if (visibleMembers.length === 0) return null;
  return { ...fund, members: visibleMembers };
}

/**
 * Main export — apply all filters across the full portals tree.
 *
 * portals: the data.portals array from /api/ledger/overview
 * filters: filter state object (see shape at top of file)
 * Returns a new portals array; empty portals are dropped.
 */
export function applyLedgerFilters(portals, filters) {
  if (!portals || portals.length === 0) return portals;

  // Treat missing filters as defaults
  const f = {
    portal: "",
    fund: "",
    investor: "",
    currency: "",
    assetClass: "",
    docTypes: [],
    dateFrom: "",
    dateTo: "",
    xirrMin: "",
    xirrMax: "",
    showExited: true,
    search: "",
    ...filters,
  };

  // If all filters are empty/default, return the original (no copy needed)
  const hasAnyFilter =
    f.portal ||
    f.fund || f.investor || f.currency || f.assetClass ||
    (f.docTypes && f.docTypes.length > 0) ||
    f.dateFrom || f.dateTo ||
    f.xirrMin || f.xirrMax ||
    f.showExited === false ||
    f.search;

  if (!hasAnyFilter) return portals;

  const result = [];
  for (const portal of portals) {
    // Portal-level filter: skip portals that don't match the selected portal
    if (f.portal && f.portal !== "" && portal.portal !== f.portal) continue;

    const filteredFunds = (portal.funds || [])
      .map((fund) => filterFund(fund, f))
      .filter(Boolean);

    if (filteredFunds.length > 0) {
      result.push({ ...portal, funds: filteredFunds });
    }
  }
  return result;
}

// ---------------------------------------------------------------------------
// Lines tab (#63) — search / filter / sort over the flat /api/ledger/lines feed.
//
// Each line: {portal, fund, investor, event_date, type, amount, currency,
// original_filename, ...}. portal/type filter values come from selects fed
// by the lines themselves and match exactly. fund/investor filter values
// match on foldTight EQUALITY (not raw ===) — the Lines dropdowns consolidate
// entries by fold-canonical identity (buildLinesFacets, linesDisplay.js), so
// a selected value may be a canonical foldTight key rather than one specific
// line's raw slug; foldTight is idempotent, so a raw slug still matches
// itself too (old exact-match callers keep working). group members are RAW
// display names while line.investor is a slug identity — fold BOTH sides
// with the module's dual-fold (foldForMatch + foldTight, see textMatches) so
// dotted acronyms cross-match either spacing style, same as the rest of this
// module (typed-filters-vs-slug-identities fix).
// ---------------------------------------------------------------------------

const LINES_SEARCH_FIELDS = ["portal", "fund", "investor", "original_filename"];

/**
 * Precompute both folds of a group's raw member names, once per group (not
 * per line) — same dual fold textMatches uses, so a dotted acronym member
 * ("I.B.I Nominees Ltd.") cross-matches a tight slug identity ("ibi nominees
 * ltd"). Entries where both folds are "" (punctuation-only names) are
 * dropped so they can never accidentally match-everything downstream.
 */
export const foldGroupMemberNames = (group) =>
  ((group && group.members) || [])
    .map((m) => ({ loose: foldForMatch(m), tight: foldTight(m) }))
    .filter((n) => n.loose || n.tight);

/**
 * Bidirectional dual-fold membership check: does `investor` (a slug
 * identity) match any of the precomputed `foldedNames` (from
 * foldGroupMemberNames)? A group member ("Emrald for Good Ltd.") and the
 * slug identity ("emrald for good ltd") may each carry tokens the other
 * lost, so this checks includes() in both directions on each fold style.
 * Fail closed: an investor with no text at all (folds to "" both ways)
 * never matches, regardless of foldedNames.
 */
export const investorMatchesFoldedNames = (investor, foldedNames) => {
  const invLoose = foldForMatch(investor);
  const invTight = foldTight(investor);
  if (!invLoose && !invTight) return false;
  return (foldedNames || []).some(
    (n) =>
      (n.loose && (invLoose.includes(n.loose) || n.loose.includes(invLoose))) ||
      (n.tight && (invTight.includes(n.tight) || n.tight.includes(invTight)))
  );
};

/**
 * applyLinesFilters(lines, filters, groups, fundDisplayNames) -> lines
 *
 * filters: { search, portal, fund, investor, group, type } — all strings,
 * ""/missing = no filter on that axis. fund/investor match on foldTight
 * equality (a canonical key from buildLinesFacets or any fold-equal raw
 * slug/typed value all match the same lines). group is a group_id resolved
 * against groups ([{group_id, name, members:[rawName,...]}]); an unknown
 * group or a group whose member names all fold to "" matches NOTHING (fail
 * closed).
 *
 * fundDisplayNames: optional {fund_slug: display_name} map (from the overview
 * payload). Lines carry only the slug fund identity; 16/123 YAD fund display
 * titles share no folded substring with their slug, so searching the title a
 * client SEES would find nothing without this (same failure class as the
 * typed-filters-vs-slug-identities fix).
 */
export function applyLinesFilters(lines, filters, groups = [], fundDisplayNames = {}) {
  if (!lines || lines.length === 0) return lines;
  const f = { search: "", portal: "", fund: "", investor: "", group: "", type: "", ...filters };
  const search = foldForMatch(f.search);
  if (!search && !f.portal && !f.fund && !f.investor && !f.group && !f.type) return lines;

  let groupNames = null; // null = no group filter; [] = group filter matching nothing
  if (f.group) {
    const g = (groups || []).find((x) => x.group_id === f.group);
    groupNames = foldGroupMemberNames(g);
  }

  return lines.filter((line) => {
    if (f.portal && line.portal !== f.portal) return false;
    if (f.fund && foldTight(line.fund) !== foldTight(f.fund)) return false;
    if (f.investor && foldTight(line.investor) !== foldTight(f.investor)) return false;
    if (f.type && line.type !== f.type) return false;
    if (groupNames && !investorMatchesFoldedNames(line.investor, groupNames)) return false;
    if (search) {
      const inFields = LINES_SEARCH_FIELDS.some((k) => textMatches(line[k], f.search));
      const inDisplay = textMatches(fundDisplayNames[line.fund], f.search);
      if (!inFields && !inDisplay) return false;
    }
    return true;
  });
}

/**
 * sortLines(lines, sortKey) -> new sorted array (input never mutated).
 * sortKey: "date_desc" (default — matches the backend's serve order),
 * "date_asc", or "type" (calls before distributions, newest first within).
 * Undated rows always sort last.
 */
export function sortLines(lines, sortKey) {
  if (!lines || lines.length === 0) return lines;
  const dateCmp = (dir) => (a, b) => {
    const da = a.event_date || "";
    const db = b.event_date || "";
    if (!da && !db) return 0;
    if (!da) return 1; // undated last regardless of direction
    if (!db) return -1;
    return da < db ? -dir : da > db ? dir : 0;
  };
  const sorted = [...lines];
  if (sortKey === "date_asc") {
    sorted.sort(dateCmp(1));
  } else if (sortKey === "type") {
    const byDateDesc = dateCmp(-1);
    // capital_call_event_amount < distribution_event_amount alphabetically —
    // calls group first, which is also the natural reading order.
    sorted.sort((a, b) => String(a.type || "").localeCompare(String(b.type || "")) || byDateDesc(a, b));
  } else {
    sorted.sort(dateCmp(-1));
  }
  return sorted;
}
